import type { KiwoomPaperFillRow } from './kiwoomPaperFillUtils';

/** AWS paper trader — designated stream (config/trader.aws.yaml) */
export const KIWOOM_LIVE_PAPER_STREAM = {
  stream_id: 'stream_5',
  stream_label: '5%',
  strategy: 'absorption_distribution',
  daily_target_pct: 5,
  initial_capital_krw: 1_000_000,
} as const;

export function todayKSTDate(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' });
}

export function isTodayKST(iso: string): boolean {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' }) === todayKSTDate();
}

export function filterTodayPaperFills(fills: KiwoomPaperFillRow[]): KiwoomPaperFillRow[] {
  return fills.filter((f) => isTodayKST(f.time));
}

export type KiwoomTodayPaperSummary = {
  tradeCount: number;
  sellCount: number;
  realizedKrw: number;
  returnPct: number | null;
  targetHit: boolean;
  symbols: string[];
};

/** 당일 매수·매도 페어 기준 실현손익 (초기자본 대비 %) */
export function summarizeTodayPaper(
  rows: KiwoomPaperFillRow[],
  dailyTargetPct: number = KIWOOM_LIVE_PAPER_STREAM.daily_target_pct,
  initialCapital: number = KIWOOM_LIVE_PAPER_STREAM.initial_capital_krw,
): KiwoomTodayPaperSummary {
  const sorted = [...rows].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
  const openBuy = new Map<string, number>();
  let realized = 0;
  let sellCount = 0;
  const symbols = new Set<string>();

  for (const f of sorted) {
    symbols.add(f.symbol);
    const side = (f.side || '').toLowerCase();
    if (side === 'buy') {
      openBuy.set(f.symbol, f.krw + (f.fee_krw ?? 0));
    } else if (side === 'sell') {
      const cost = openBuy.get(f.symbol) ?? 0;
      realized += f.krw - cost - (f.fee_krw ?? 0);
      sellCount += 1;
      openBuy.delete(f.symbol);
    }
  }

  const targetKrw = initialCapital * (dailyTargetPct / 100);
  const returnPct = initialCapital > 0 ? (realized / initialCapital) * 100 : null;

  return {
    tradeCount: rows.length,
    sellCount,
    realizedKrw: realized,
    returnPct,
    targetHit: realized >= targetKrw * 0.98,
    symbols: Array.from(symbols),
  };
}

export type KiwoomBacktestStreamDay = {
  stream_id: string;
  stream_label: string;
  daily_target_pct?: number;
  strategy?: string;
  target_hit?: boolean;
  total_return_pct?: number;
  trade_count?: number;
  skipped?: boolean;
  skip_reason?: string;
  trades?: Array<{
    time?: string;
    symbol?: string;
    side?: string;
    price?: number;
    pnl_krw?: number;
  }>;
};

export type KiwoomBacktestAllPayload = {
  generated_at?: string;
  initial_capital_krw?: number;
  streams?: Array<{
    stream_id?: string;
    stream_label?: string;
    daily_target_pct?: number;
    strategy?: string;
    results?: Array<{
      trade_date?: string;
      target_hit?: boolean;
      total_return_pct?: number;
      trade_count?: number;
      trades?: KiwoomBacktestStreamDay['trades'];
    }>;
  }>;
};

export const KIWOOM_DEFAULT_STREAM_ORDER = ['stream_5', 'stream_75', 'stream_10', 'stream_125', 'stream_15'] as const;

/** Paper/designated stream id — from pipeline S3, fills S3, or config default (currently stream_5). */
export function designatedPaperStreamId(
  pipeline?: { primary_stream?: string } | null,
  fills?: { stream_id?: string } | null,
): string {
  return pipeline?.primary_stream || fills?.stream_id || KIWOOM_LIVE_PAPER_STREAM.stream_id;
}

/** Display label for a stream from S3 fills metadata or defaults. */
export function streamDisplayLabel(
  streamId: string,
  fills?: { stream_label?: string } | null,
): string {
  if (fills?.stream_label) return fills.stream_label;
  return KIWOOM_STREAM_LABELS[streamId] ?? streamId;
}

export function isDesignatedPaperStream(streamId: string, designatedId: string): boolean {
  return streamId === designatedId;
}

export const KIWOOM_STREAM_LABELS: Record<string, string> = {
  stream_5: '5%',
  stream_75: '7.5%',
  stream_10: '10%',
  stream_125: '12.5%',
  stream_15: '15%',
};

export const KIWOOM_STREAM_TARGET_PCT: Record<string, number> = {
  stream_5: 5,
  stream_75: 7.5,
  stream_10: 10,
  stream_125: 12.5,
  stream_15: 15,
};

export function designatedPaperStreamLabel(designatedId: string): string {
  return KIWOOM_STREAM_LABELS[designatedId] ?? designatedId;
}

export function backtestStreamsFromPipeline(
  snap: import('./kiwoomApi').KiwoomPipelineS3Snapshot | null | undefined,
): KiwoomBacktestStreamDay[] {
  if (!snap?.streams) return [];
  const order = snap.stream_order ?? [...KIWOOM_DEFAULT_STREAM_ORDER];
  const out: KiwoomBacktestStreamDay[] = [];
  for (const streamId of order) {
    const st = snap.streams[streamId];
    const last = st?.daily?.last;
    if (!last) continue;
    out.push({
      stream_id: streamId,
      stream_label: KIWOOM_STREAM_LABELS[streamId] ?? streamId,
      daily_target_pct: last.daily_target_pct ?? KIWOOM_STREAM_TARGET_PCT[streamId],
      strategy: last.strategy,
      target_hit: last.daily_target === 'HIT',
      total_return_pct: last.return_pct,
      trade_count: last.round_trips,
    });
  }
  return out;
}

export type TodayStreamPanelOpts = {
  paperTargetHit?: boolean;
  paperReturnPct?: number | null;
  paperStrategy?: string;
  /** 지정 스트림 (primary_stream) */
  designatedStreamId?: string;
};

/** 오늘의 결과 — 지정 스트림 paper 완료 시 장후 백테스트 SKIP, 나머지는 pipeline S3 */
export function todayStreamResultsForPanel(
  snap: import('./kiwoomApi').KiwoomPipelineS3Snapshot | null | undefined,
  opts: TodayStreamPanelOpts = {},
): KiwoomBacktestStreamDay[] {
  const order = snap?.stream_order ?? [...KIWOOM_DEFAULT_STREAM_ORDER];
  const designated = opts.designatedStreamId ?? designatedPaperStreamId(snap);
  return order.map((streamId) => {
    if (isDesignatedPaperStream(streamId, designated)) {
      const st = snap?.streams?.[designated];
      if (opts.paperTargetHit || st?.regression?.skipped) {
        return {
          stream_id: designated,
          stream_label: KIWOOM_STREAM_LABELS[designated] ?? designated,
          daily_target_pct: KIWOOM_STREAM_TARGET_PCT[designated] ?? KIWOOM_LIVE_PAPER_STREAM.daily_target_pct,
          strategy: opts.paperStrategy ?? KIWOOM_LIVE_PAPER_STREAM.strategy,
          skipped: true,
          skip_reason: '지정 스트림 paper 완료 — 장후 백테스트 생략',
          target_hit: opts.paperTargetHit ?? st?.daily?.hit,
          total_return_pct: opts.paperReturnPct ?? st?.daily?.last?.return_pct ?? undefined,
        };
      }
    }
    const st = snap?.streams?.[streamId];
    const last = st?.daily?.last;
    if (!last) {
      return {
        stream_id: streamId,
        stream_label: KIWOOM_STREAM_LABELS[streamId] ?? streamId,
        daily_target_pct: KIWOOM_STREAM_TARGET_PCT[streamId],
      };
    }
    return {
      stream_id: streamId,
      stream_label: KIWOOM_STREAM_LABELS[streamId] ?? streamId,
      daily_target_pct: last.daily_target_pct ?? KIWOOM_STREAM_TARGET_PCT[streamId],
      strategy: last.strategy,
      target_hit: last.daily_target === 'HIT',
      total_return_pct: last.return_pct,
      trade_count: last.round_trips,
    };
  });
}

/** @deprecated S3 pipeline snapshot preferred — use backtestStreamsFromPipeline */
export function backtestStreamsForToday(payload: KiwoomBacktestAllPayload | null): KiwoomBacktestStreamDay[] {
  if (!payload?.streams?.length) return [];
  const today = todayKSTDate();
  const out: KiwoomBacktestStreamDay[] = [];
  for (const s of payload.streams) {
    const day = s.results?.find((r) => r.trade_date === today);
    if (!day) continue;
    const id = s.stream_id ?? s.stream_id ?? '';
    out.push({
      stream_id: id,
      stream_label: s.stream_label ?? s.stream_label ?? (id || '–'),
      daily_target_pct: s.daily_target_pct,
      strategy: s.strategy,
      target_hit: day.target_hit,
      total_return_pct: day.total_return_pct,
      trade_count: day.trade_count ?? day.trades?.length,
      trades: day.trades,
    });
  }
  return out;
}
